import { html, css, render } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { router, hasAccess, views, ViewRoute } from '../app'
import { inject, Symbols } from '../di'
import { MeStore, NotesStore } from '../stores'
import { NotesView } from './notes'
import { Layout, View } from './view'
import { mainStyles } from '../vaadin/main-layout'
import { Router } from '@vaadin/router'

const coffeeIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>`
const bellIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>`
const helpIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`

@customElement('kob-main')
export class MainLayout extends Layout {

    @inject(Symbols.stores.me)
    meStore: MeStore

    @inject(Symbols.stores.notes)
    notesStore: NotesStore

    @query('kob-notes')
    notesDialog: NotesView

    @property({ type: Object })
    selectedRoute

    static get styles() {
        return [
            mainStyles,
            css`
                .content {
                    overflow: hidden;
                    height: calc(100vh - var(--kop-header-height));
                }
            `
        ]
    }

    handleMenuClicked = async (e) => {
        if (e.target.value === 'logout')
            await this.meStore.logout()
        if (e.target.value === 'notes')
            await this.notesDialog.open()
    }

    highlight = (viewRoute) => viewRoute.path === window.location.pathname

    private get menuRoutes(): ViewRoute[] {
        return views.filter((route) => route.title && hasAccess(route))
    }

    navigate = (route: ViewRoute) => (e: MouseEvent) => {
        // Let cmd/ctrl-click and middle-click fall through to the browser's new-tab handling.
        if (e.metaKey || e.ctrlKey || e.button !== 0) return
        e.preventDefault()
        if (route.path !== window.location.pathname)
            Router.go(route.path as string)
    }

    render() {
        return html`
            <kob-notes></kob-notes>
            <vaadin-app-layout>
                <header slot="navbar">
                    <div class="app-header-top">
                        <div class="app-logo">
                            <svg width="22" height="32" viewBox="0 0 50 72" aria-hidden="true">
                                <path d="M.5 47.288c2.884-10.3 16.58-16.217 21.833-27.02 2.541-5.228 1.353-14.881-2.986-18.747-.23-.206-1.186-2.06-.213-1.366 9.668 7.074 17.529 16.413 13.892 25.427-5.046 12.51-18.043 18.758-17.948 31.496.041 5.299 3.945 9.55 8.909 13.994l.203.929C11.3 68.322-2.858 59.285.5 47.288Zm32.53 13.987c7.488-6.734 12.06-10.826 10.126-18.625 2.351 3.607 4.534 8.611 2.67 12.51-1.668 3.486-3.328 5.914-9.402 9.312-3.22 1.8-5.64 3.75-7.661 7.012-.106-3.415.553-6.867 4.268-10.209Zm-8.31-11.594c2.863-5.268 7.044-9.038 11.095-14.442 2.876-3.836 5.51-10.206 2.757-15.406l.177-.25c4.22 4.715 4.395 12.4 3.072 18.093-2.08 8.952-9.492 12.569-13.799 19.114-1.687 2.564-2.646 5.704-2.168 8.994-4.782-3.903-3.697-11.387-1.134-16.103Z" fill="currentColor"/>
                            </svg>
                            <span class="word">Koblime</span>
                        </div>
                        <vaadin-context-menu class="app-account" open-on="click" .renderer=${this.renderLogoutOptions}>
                            <vaadin-avatar theme="xsmall" img="${this.meStore.profileImage}" name="${this.meStore.fullName}">
                            </vaadin-avatar>
                            <span>${this.meStore.fullName}</span>
                            <vaadin-icon icon="lumo:angle-down"></vaadin-icon>
                        </vaadin-context-menu>
                    </div>
                    <nav class="app-tabs" aria-label="Primary">
                        ${this.menuRoutes.map((route) => html`
                            <a
                                class="app-tab ${this.highlight(route) ? 'active' : ''}"
                                href=${route.path}
                                @click=${this.navigate(route)}
                            >${route.title}</a>
                        `)}
                        <span class="app-tabs-spacer"></span>
                        <button
                            type="button"
                            class="icon-link bell"
                            title="What's new"
                            aria-label="${this.meStore.hasUnseenNote ? "What's new, unread" : "What's new"}"
                            @click=${() => this.notesDialog?.open()}
                        >
                            ${bellIcon}
                            ${this.meStore.hasUnseenNote
                                ? html`<span class="bell-badge" aria-hidden="true"></span>` : ''}
                        </button>
                        <a class="icon-link" href="https://www.buymeacoffee.com/koblime/membership" target="_blank" title="Buy me a coffee">
                            ${coffeeIcon}
                        </a>
                        <a class="icon-link" href="https://docs.kobli.me" target="_blank" title="Help">
                            ${helpIcon}
                        </a>
                    </nav>
                </header>
                <div class="content">
                    <slot></slot>
                </div>
            </vaadin-app-layout>
        `
    }

    private renderLogoutOptions = (root: HTMLElement) => {
        render(html`
            <vaadin-list-box @click=${this.handleMenuClicked}>
                <vaadin-item value="notes" style="cursor:pointer;">
                    What's new
                    ${this.meStore.hasUnseenNote ? html`<span theme="badge info small">New</span>` : ''}
                </vaadin-item>
                <vaadin-item value="logout" style="cursor:pointer;">Logout</vaadin-item>
            </vaadin-list-box>`
            , root)
    }

    async connectedCallback() {
        await super.connectedCallback()
        // The following triggers a refresh of the tabs so the underline follows the route
        const refresh = () => this.selectedRoute = window.location.pathname
        window.addEventListener('popstate', refresh)
        window.addEventListener('vaadin-router-location-changed', refresh)

        await this.showUnseenNotes()
    }

    /** Once auth has resolved, greet a signed-in account with anything it hasn't seen. */
    private async showUnseenNotes() {
        await this.meStore.initialized
        if (!this.meStore.isAuthenticated) return

        // Decided from the marker on the user doc, so a caught-up account makes
        // no notes request at all.
        if (!this.meStore.hasUnseenNote) return

        await this.updateComplete
        await this.notesDialog?.open()
    }
}
